import { useState, Fragment } from "react";
import { Stack, Dialog, Button, Box, DialogTitle, DialogContent, Typography, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Smartphone } from '@mui/icons-material';
import { CloseIconButton } from '../Utilities';
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";

import QrProjekteApp from '../../assets/qr_projekte_app.png';
import QrProjekteTestApp from '../../assets/qr_projekte_test_app.png';
import AppleButtonDeDark from '../../assets/apple_de_dark.png';
import AppleButtonDeLight from '../../assets/apple_de_light.png';
import AppleButtonNlDark from '../../assets/apple_nl_dark.png';
import AppleButtonNlLight from '../../assets/apple_nl_light.png';
import AppleButtonEnDark from '../../assets/apple_en_dark.png';
import AppleButtonEnLight from '../../assets/apple_en_light.png';
import GoogleButtonDeDark from '../../assets/google_de_dark.png';
import GoogleButtonDeLight from '../../assets/google_de_light.png';
import GoogleButtonNlDark from '../../assets/google_nl_dark.png';
import GoogleButtonNlLight from '../../assets/google_nl_light.png';
import GoogleButtonEnDark from '../../assets/google_en_dark.png';
import GoogleButtonEnLight from '../../assets/google_en_light.png';

export default function AppStoreLinks({ invertColors }) {

    const [openDialog, setOpenDialog] = useState(false);
    
    const { t, i18n } = useTranslation();
    const { APP: { REDIRECT, STORE: { GOOGLE, APPLE } } } = window.conf;

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    const getQrCode = REDIRECT.includes("test") ? QrProjekteTestApp : QrProjekteApp;

    const getStoreButton = (store) => {
        const locale = i18n.language.slice(0, 2).toLowerCase();

        const buttons = {
            apple: {
                de: { light: AppleButtonDeLight, dark: AppleButtonDeDark },
                nl: { light: AppleButtonNlLight, dark: AppleButtonNlDark },
                en: { light: AppleButtonEnLight, dark: AppleButtonEnDark }
            },
            google: {
                de: { light: GoogleButtonDeLight, dark: GoogleButtonDeDark },
                nl: { light: GoogleButtonNlLight, dark: GoogleButtonNlDark },
                en: { light: GoogleButtonEnLight, dark: GoogleButtonEnDark }
            }
        };

        const storeButtons = buttons[store];
        const selectedLang = storeButtons[locale] || storeButtons.de;

        return invertColors ? selectedLang.light : selectedLang.dark;
    };

    return (
        <Fragment>

            {/* button to open the dialog */}

            <Button
                startIcon={<Smartphone />}
                variant={invertColors ? "outlined" : "text"}
                onClick={handleOpenDialog}
                sx={{
                    height: { xs: '29px', sm: '39px' },
                    color: invertColors ? "primary.main" : "white",
                    backgroundColor: invertColors ? 'white' : 'primary.main',
                    textTransform: 'none',
                    fontSize: { xs: '13px', sm: '14px' },
                    px: { xs: '8px', sm: '16px' },
                    '& .MuiButton-startIcon': { mr: { xs: '4px', sm: '6px' } },
                    '&:hover': {
                        backgroundColor: invertColors ? 'white' : 'primary.main',
                        color: invertColors ? 'primary.main' : 'white',
                        boxShadow: 'none',
                        borderColor: invertColors ? 'white' : undefined
                    },
                    mr: "16px",
                    ml: "24px"
                }}
            >
                <Box sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {isMobile ? t("app.download.short") : t("app.download.long")}
                </Box>
            </Button>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: invertColors ? 'primary.main' : 'white',
                        color: invertColors ? 'white' : 'text.primary',
                        textAlign: 'center',
                        maxWidth: 250
                    }
                }}
            >
                <CloseIconButton onClick={handleCloseDialog} />

                {/* title */}

                <DialogTitle sx={{ userSelect: "none", fontSize: "1.3rem", color: invertColors ? 'white' : 'text.primary' }}>
                    {t("app.download.long")}
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2} alignItems="center">

                        {/* qr code */}

                        <Link to={REDIRECT} target="_blank" rel="noopener noreferrer">
                            <Box
                                component="img"
                                src={getQrCode}
                                alt="QR Code"
                                sx={{
                                    maxWidth: invertColors ? 159: 179,
                                    width: '100%',
                                    borderRadius: 2,
                                    border: invertColors ? '2px solid black' : null,
                                    p: invertColors ? 1 : 0,
                                    backgroundColor: invertColors ? 'white' : 'transparent'
                                }}
                            />
                        </Link>

                        {/* app store button */}

                        <Link to={APPLE} target="_blank" rel="noopener noreferrer">
                            <Box component="img" src={getStoreButton('apple')} alt="Apple Store" sx={{ maxWidth: 180, width: '100%' }} />
                        </Link>

                        {/* google play button */}

                        <Link to={GOOGLE} target="_blank" rel="noopener noreferrer">
                            <Box component="img" src={getStoreButton('google')} alt="Google Play" sx={{ maxWidth: 180, width: '100%' }} />
                        </Link>

                        <Divider flexItem sx={{ borderColor: invertColors ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}} />

                        {/* legal line */}

                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.7rem',
                                color: invertColors ? 'white' : 'text.primary',
                                mt: 2,
                                px: 2,
                                userSelect: "none"
                            }}
                        >
                            {t("app.trademark")}
                        </Typography>

                    </Stack>
                </DialogContent>
                
            </Dialog>
        </Fragment>
    )
}